import * as React from 'react'
import {
  type AnimationOptions,
  type HTMLMotionProps,
  motion,
  MotionConfigContext,
  useIsPresent,
} from 'motion/react'

import type { Justify } from './types'
import styles from './Digit.module.css'
import { useAnimateNumber } from './hooks/useAnimateNumber'

type WholeNumberProps = Omit<HTMLMotionProps<'span'>, 'children'> & {
  value: number
  initialValue?: number
  layoutDependency?: unknown
  justify?: Justify
}

const NumberStack = ({ value }: { value: number }): JSX.Element => {
  const numbersBelow = React.useMemo(() => {
    // For negative numbers, show 10 numbers below
    if (value < 0) {
      return Array.from({ length: 10 }, (_, i) => (
        <span key={i} className={styles.digit}>
          {value - 10 + i}
        </span>
      ))
    }
    // For 0, show 10 negative numbers below
    if (value === 0) {
      return Array.from({ length: 10 }, (_, i) => (
        <span key={i} className={styles.digit}>
          {-10 + i}
        </span>
      ))
    }
    // For positive numbers, show numbers from 0 to current value
    return Array.from({ length: value }, (_, i) => (
      <span key={i} className={styles.digit}>
        {i}
      </span>
    ))
  }, [value])

  const numbersAbove = React.useMemo(() => {
    // For negative numbers, show 10 numbers above
    if (value < 0) {
      return Array.from({ length: 10 }, (_, i) => (
        <span key={i} className={styles.digit}>
          {value + i + 1}
        </span>
      ))
    }
    // For positive numbers, show 10 numbers above
    if (value === 999999) return null // Reasonable upper limit
    return Array.from({ length: 10 }, (_, i) => (
      <span key={i} className={styles.digit}>
        {value + i + 1}
      </span>
    ))
  }, [value])

  return (
    <>
      {numbersBelow && (
        <span className={`${styles.digitStack} ${styles.digitStackBottom}`}>
          {numbersBelow}
        </span>
      )}
      <span className={styles.digit}>{value}</span>
      {numbersAbove && (
        <span className={`${styles.digitStack} ${styles.digitStackTop}`}>
          {numbersAbove}
        </span>
      )}
    </>
  )
}

/**
 * WholeNumber component that renders a number with smooth transitions
 * Uses FLIP animation technique for smooth number changes
 */
export const WholeNumber = React.forwardRef<HTMLSpanElement, WholeNumberProps>(
  (
    { value: _value, initialValue: _initialValue = _value, ...rest },
    _ref,
  ): JSX.Element => {
    const { transition } = React.useContext(MotionConfigContext)
    const initialValue = React.useRef(_initialValue).current

    const animateRef = React.useRef<HTMLSpanElement>(null)
    const widthRef = React.useRef<HTMLSpanElement>(null)
    React.useImperativeHandle(_ref, () => widthRef.current!, [])

    const isPresent = useIsPresent()
    const displayValue = isPresent ? _value : 0

    useAnimateNumber(
      animateRef,
      widthRef,
      displayValue,
      initialValue,
      transition as AnimationOptions,
    )

    return (
      <motion.span
        {...rest}
        data-testid="whole-number"
        ref={widthRef}
        layout="position"
        data-state={isPresent ? undefined : 'exiting'}
        className={styles.digitContainer}
      >
        <span ref={animateRef} className={styles.digitStackWrapper}>
          <NumberStack value={displayValue} />
        </span>
      </motion.span>
    )
  },
)

WholeNumber.displayName = 'WholeNumber'
